import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MailCheck } from "lucide-react";
import { authAPI } from "../../../services/apiService";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Showcase } from "../../components/auth/Showcase";
import { FormError } from "../../components/auth/fields";
import { AUTH_PATHS } from "../../components/auth/signupState";
import { Field, TextInput, isEmail } from "../../components/crm/leadsKit";
import { PrimaryButton } from "../../components/ui";

/**
 * Forgot password (design: screens/ForgotPassword.tsx) on the same call as the
 * old page: POST /api/auth/forgot-password { email } (trimmed, lower-cased).
 * The email links to /reset-password?token=…, which stays on the old
 * pages/ResetPassword.js (the design has no reset screen).
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isEmail(email)) {
      setError(email.trim() ? "Enter an email like name@clinic.in" : "Enter your work email");
      return;
    }
    setError("");
    setFormError("");
    setLoading(true);
    try {
      const r = await authAPI.forgotPassword(email.trim().toLowerCase());
      if (r.data.success) setSent(true);
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout aside={<Showcase step="forgot" />}>
      <div className="flex flex-col gap-8">
        <Link to={AUTH_PATHS.signin} className="flex items-center gap-2 self-start text-small font-medium text-muted hover:text-ink">
          <ArrowLeft size={16} strokeWidth={1.75} />
          Back to sign in
        </Link>

        {sent ? (
          <div key="sent" className="flex animate-fade-in flex-col gap-6">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sun">
              <MailCheck size={24} strokeWidth={1.75} />
            </span>
            <div className="flex flex-col gap-2">
              <h1 className="text-h1 font-semibold">Check your email</h1>
              <p className="text-body text-muted">
                If <span className="font-medium text-ink">{email.trim()}</span> has an Olli account, a reset link is on its way. It expires in 1 hour.
              </p>
            </div>
            <button type="button" onClick={() => setSent(false)} className="self-start text-small font-medium text-sky-deep hover:underline">
              Use a different email
            </button>
          </div>
        ) : (
          <form key="form" onSubmit={submit} noValidate className="flex animate-fade-in flex-col gap-8">
            <div className="flex flex-col gap-2">
              <h1 className="text-h1 font-semibold">Reset your password</h1>
              <p className="text-body text-muted">We'll email you a link to set a new one</p>
            </div>
            {formError && <FormError>{formError}</FormError>}
            <Field id="email" label="Work email" error={error}>
              <TextInput id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} invalid={!!error} />
            </Field>
            <PrimaryButton type="submit" loading={loading} loadingLabel="Sending link…">
              Send reset link
            </PrimaryButton>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
